import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogData } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl, SafeResourceUrl } from '@angular/platform-browser';
import { MatPaginator } from '@angular/material/paginator';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { DataService } from 'src/app/core/services/data.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CalendarEvent, CalendarView, CalendarWeekViewBeforeRenderEvent } from 'angular-calendar';
import { DatePipe } from '@angular/common';
import { addDays, differenceInMinutes, startOfDay, startOfHour, subDays, subWeeks } from 'date-fns';
import { reduce, Subject } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DeleteConfirmComponent } from '../../notifications/delete-confirm/delete-confirm.component';

interface Appointment {
  id: string;
  appointment_from: string;
  appointment_to: string;
  consultation_fee: string;
  total_amount: string;
  amount_paid: string;
  appointment_status: string;
  payment_status: string;
  payment_method: string | null;
  created_at: string;
  lawyerName?: string;
  lawyer_profile_pic?: string; 
  lawyer_name?: string;
  lawyerInitials?: string;
  specialization?: string;
  status?: 'Refunded' | 'Paid' ;
  subStatus?: 'Wallet' | 'Credit Card' | 'UPI';
  dateTime?: string;
  note?: string;
  type?: 'Instant' | 'Scheduled';
  amount?: any;
}

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.css'],
    standalone: false
})
export class SubscriptionComponent implements OnInit {

  currentTime = new Date();
  appointments: Appointment[] = [];
  filteredAppointments: Appointment[] = [];
  searchQuery = '';
  statusFilter = 'all';
  statusLawyer = 'All Lawyers';
  sortBy = '';
  displayedColumns = ['lawyer', 'datetime', 'features', 'type', 'status', 'actions'];

  // ── Invoice viewer state (shared invoice-pdf-viewer component) ──────────
  isInvoicePreviewOpen = false;
  isInvoicePreviewLoading = false;
  invoicePreviewUrl: SafeResourceUrl | null = null;
  invoiceTitle = 'Invoice';
  private invoiceRequestInFlight = false;
  private invoiceDownloadInFlight = false;
  pageIndex = 0;
  pageSize = 10;
  pagedAppointments: any[] = [];
  Math = Math;
  activeTab: string = 'personal';
  isLoading = false;
  lawyerOptions: string[] = ['All Lawyers'];
  sortColumn: string = 'date';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private auth: AuthService,
    private data: DataService,
    private router: Router,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.fetchAppointments();
    this.statusFilter = 'all'; // Set status val to 'all'
  }

  fetchAppointments() {
    this.isLoading = true;
    this.auth.getAppointmentsHistory().subscribe({
      next: (res: any) => {
        if (!res.error && res.appointments) {
          this.appointments = res.appointments.map((apt: any) => this.mapAppointment(apt));
          this.filteredAppointments = [...this.appointments];

          // FIX 3: Trim names when building options to prevent hidden whitespace mismatches
          const uniqueLawyers = [...new Set(
            this.appointments
              .map(a => a.lawyerName?.trim())
              .filter((name): name is string => !!name)
          )];
          this.lawyerOptions = ['All Lawyers', ...uniqueLawyers];
          this.updatePagedData();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching appointments:', error);
        this.toastr.error('Failed to load appointments');
        this.isLoading = false;
      }
    });
  }

private mapAppointment(apiData: any): Appointment {

  // ✅ convert text to Title Case safely
  const toTitleCase = (value: string) =>
    value
      ? value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
      : value;

  const displayStatus = apiData.payment_status
    ? toTitleCase(apiData.payment_status)
    : 'Pending';

  const subStatus = apiData.payment_method
    ? toTitleCase(apiData.payment_method)
    : 'Credit Card';

  const rawName = (apiData.lawyer_name ?? apiData.lawyerName ?? apiData.name ?? '')
    .toString()
    .trim();

  // ✅ Format date & time normally
  const appointmentDate = new Date(apiData.appointment_from);

  // Amount Paid column: for refunded/refund-initiated payments or cancelled
  // appointments, show the consultation_fee; otherwise show amount_paid.
  const isRefundedOrCancelled =
    (apiData.payment_status || '').toString().toLowerCase() === 'refunded' ||
    (apiData.payment_status || '').toString().toLowerCase() === 'refund_initiated' ||
    (apiData.appointment_status || '').toString().toLowerCase() === 'cancelled';

  const displayAmount = isRefundedOrCancelled
    ? apiData.consultation_fee
    : apiData.amount_paid;

  return {
    ...apiData,

    lawyerName: rawName,

    lawyerInitials: rawName
      ? rawName.split(' ').map((w: string) => w[0]).join('').substring(0, 1).toUpperCase()
      : 'L',

    specialization: apiData.specialization?.[0],
    lawyer_profile_pic: apiData.lawyer_profile_pic || null,

    status: displayStatus,
    subStatus: subStatus,

    dateTime: appointmentDate,

    amount: displayAmount,

    note: undefined 
  };
}
  onSearchChange() {
    this.applyFilters();
  }

  onStatusChange(value: string) {
    this.statusFilter = value;
    this.applyFilters();
  }

  sortByColumn(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  applyFilters() {
    const q = this.searchQuery.toLowerCase().trim();

    let data = this.appointments.filter(a => {
      // Format the date for searching (e.g. "Mon, Feb 17, 2:30 PM")
      const formattedDate = a.dateTime
        ? new Date(a.dateTime).toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: '2-digit',
            hour: 'numeric', minute: '2-digit', hour12: true
          }).toLowerCase()
        : '';

      const matchesSearch = !q ||
        (a.lawyerName?.toLowerCase().includes(q) || false) ||
        (a.specialization?.toLowerCase().includes(q) || false) ||
        (a.payment_status?.toLowerCase().includes(q) || false) ||
        (a.subStatus?.toLowerCase().includes(q) || false) ||
        (a.amount?.toString().toLowerCase().includes(q) || false) ||
        formattedDate.includes(q);

      //const matchesStatus = this.statusFilter === 'All Status' || a.payment_status === this.statusFilter;
      const matchesStatus = this.statusFilter === 'all' || a.payment_status?.toLowerCase() === this.statusFilter;
      const selectedLawyer = this.statusLawyer?.trim().replace(/\s+/g, ' ').toLowerCase();
      const matchesLawyer =
        selectedLawyer === 'all lawyers' ||
        (a.lawyerName?.trim().replace(/\s+/g, ' ').toLowerCase() === selectedLawyer);

      return matchesSearch && matchesStatus && matchesLawyer;
    });

if (this.sortColumn === 'lawyer') {
  // Column header click: sort by lawyer name with toggle direction
  data.sort((a, b) => {
    const cmp = (a.lawyerName || '').localeCompare(b.lawyerName || '');
    return this.sortDirection === 'asc' ? cmp : -cmp;
  });
} else if (this.sortColumn === 'amount') {
  data.sort((a, b) => {
    const cmp = parseFloat(a.amount || '0') - parseFloat(b.amount || '0');
    return this.sortDirection === 'asc' ? cmp : -cmp;
  });
} else {
  // sortColumn === 'date' — combine with sortBy dropdown
  data.sort((a, b) => {
    if (this.sortBy === 'name') {
      // Dropdown "By Name" overrides column sort
      return (a.lawyerName || '').localeCompare(b.lawyerName || '');
    }
    const cmp = new Date(a.appointment_from).getTime() - new Date(b.appointment_from).getTime();
    // sortBy === 'date' means Oldest First (asc), '' means Latest First (desc)
    return this.sortBy === 'date' ? cmp : -cmp;
  });
}

    this.filteredAppointments = data;
    this.pageIndex = 0;
    this.updatePagedData();
  }

  goBack() {
    this.router.navigate(['/consumer/appointments']);
  }

  updatePagedData() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.pagedAppointments = this.filteredAppointments.slice(start, end);
  }

  nextPage() {
    if ((this.pageIndex + 1) * this.pageSize < this.filteredAppointments.length) {
      this.pageIndex++;
      this.updatePagedData();
    }
  }

  prevPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.updatePagedData();
    }
  }

  get endIndex() {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredAppointments.length);
  }

  gooApp() {
    window.location.href = "/appointments";
  }

  filterAppointments(): void {
    this.filteredAppointments = this.appointments.filter(a =>
      a.lawyerName?.toLowerCase().includes(this.searchQuery.toLowerCase()) &&
      (this.statusFilter === 'All Status' || a.status === this.statusFilter)
    );
    this.pageIndex = 0;
    this.updatePagedData();
  }

  onLawyerChange(status: string): void {
    this.statusLawyer = status;
    this.applyFilters();
  }

  bookApp() {
    const link = `https://lauditor.com`;
    window.open(link);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  goChat() {
    this.router.navigate(['/consumer/chats']);
  }

  goPayment() {
    this.router.navigate(['/consumer/subscription']);
  }

  // ── View Invoice (Payment History) ───────────────────────────────────────
  // Uses the payment record's own `id` (the appointment id — the payment
  // history list is built from the same appointment records as the
  // Appointments module's getAppointmentsHistory() response), never
  // payment_id/transaction_id/lawyer_id.
  viewInvoice(a: any): void {
    if (!a?.id) {
      this.toastr.error('Unable to view invoice: missing appointment id.');
      return;
    }
    if (this.invoiceRequestInFlight) {
      return;
    }

    this.invoiceRequestInFlight = true;
    this.invoiceTitle = `Invoice - ${a.lawyerName || ''}`.trim();
    this.invoicePreviewUrl = null;
    this.isInvoicePreviewOpen = true;
    this.isInvoicePreviewLoading = true;

    this.auth.viewInvoice(a.id,'view').subscribe({
      next: (res: any) => {
        this.invoiceRequestInFlight = false;
        this.isInvoicePreviewLoading = false;

        // See the note on AuthService.viewInvoice(): assumes the same
        // JSON { error:false, data:{ url } } shape used by the existing
        // Documents download endpoints, unless confirmed otherwise.
        const pdfUrl = res?.data?.url || res?.url || (typeof res === 'string' ? res : null);

        if (res?.error || !pdfUrl) {
          this.isInvoicePreviewOpen = false;
          this.toastr.error(res?.msg || 'Failed to load invoice.');
          return;
        }

        this.invoicePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
      },
      error: (err) => {
        this.invoiceRequestInFlight = false;
        this.isInvoicePreviewLoading = false;
        this.isInvoicePreviewOpen = false;
        const message = err?.error?.msg || 'Failed to load invoice.';
        this.toastr.error(message);
      }
    });
  }

  closeInvoicePreview(): void {
    this.isInvoicePreviewOpen = false;
    this.isInvoicePreviewLoading = false;
    this.invoicePreviewUrl = null;
  }

  // ── Download Invoice (Payment History) ───────────────────────────────────
  // Reuses the same "open the URL returned by the API" pattern already used
  // by the Documents module's download_personal()/download_credentials()
  // (see documents.component.ts), rather than introducing a new blob/download
  // mechanism.
  downloadInvoice(a: any): void {
    if (!a?.id) {
      this.toastr.error('Unable to download invoice: missing appointment id.');
      return;
    }
    if (this.invoiceDownloadInFlight) {
      return;
    }

    this.invoiceDownloadInFlight = true;

    this.auth.viewInvoice(a.id, 'download').subscribe({
      next: (res: any) => {
        this.invoiceDownloadInFlight = false;

        const fileUrl = res?.data?.url || res?.url;
        if (res?.error || !fileUrl) {
          this.toastr.error(res?.msg || 'Failed to download invoice.');
          return;
        }

        // Matches documents.component.ts's download_personal()/download_credential():
        // open the returned URL so the browser handles the actual file download.
        window.open(fileUrl, '_blank');
        this.toastr.success('Invoice downloaded successfully');
      },
      error: (err) => {
        this.invoiceDownloadInFlight = false;
        const message = err?.error?.msg || 'Failed to download invoice.';
        this.toastr.error(message);
      }
    });
  }

  getTotalPaid(): number {
    return this.appointments
      .filter(a => (a.payment_status || '').toLowerCase() === 'paid')
      .reduce((sum, a) => sum + parseFloat(a.amount || '0'), 0);
  }

  getTotalRefunded(): number {
    // API sends payment_status as "Refunded" (title-case), so compare case-insensitively.
    return this.appointments
      .filter(a => (a.payment_status || '').toLowerCase() === 'refunded')
      .reduce((sum, a) => sum + parseFloat(a.amount || '0'), 0);
  }

  getTotalRefundInitiated(): number {
    return this.appointments
      .filter(a => (a.payment_status || '').toLowerCase() === 'refund_initiated')
      .reduce((sum, a) => sum + parseFloat(a.amount || '0'), 0);
  }

  getTotalTransactions(): number {
    return this.appointments.length;
  }
}